// ============================================================================
// ConsequencePreview — the "What will happen" box in a form
// ============================================================================
// States the outcome of the action the user is about to confirm, in the user's
// terms, from the values they have entered. Use it on any action that is
// date-based, affects other records, or cannot be undone — never for generic
// help text (help content is served from the API, not the bundle).
//
// Usually not rendered directly: pass `consequence` to EnterpriseFormModal,
// which shows this box under the fields, repeats the same sentences in the
// save confirmation, and turns the save button red for a `danger` outcome.
//
// Tones:
//   info    — the normal outcome, stated plainly
//   warning — allowed, with a side effect worth a second look
//   danger  — will block other work or cannot be undone
// ============================================================================

import React from 'react';
import IconSystem from 'icon-collection/icon-systems';
import './guidance.css';

export type ConsequenceTone = 'info' | 'warning' | 'danger';

export interface Consequence {
  tone: ConsequenceTone;
  /** One sentence per line, plain text — also used verbatim in the confirmation dialog. */
  lines: string[];
  /** Default "What will happen". */
  title?: string;
}

interface Props {
  consequence: Consequence;
}

export const ConsequencePreview: React.FC<Props> = ({ consequence }) => {
  const { tone, lines, title = 'What will happen' } = consequence;
  if (lines.length === 0) return null;
  const Icon = tone === 'info' ? IconSystem.info : IconSystem.warning;

  return (
    <div className={`es-consequence es-tone-${tone}`} role={tone === 'danger' ? 'alert' : 'status'} aria-live="polite">
      <Icon size={18} className="es-consequence-icon" />
      <div className="es-consequence-body">
        <div className="es-consequence-title">{title}</div>
        {lines.length === 1 ? (
          <div>{lines[0]}</div>
        ) : (
          <ul className="es-consequence-list">
            {lines.map((line) => <li key={line}>{line}</li>)}
          </ul>
        )}
      </div>
    </div>
  );
};

/** The confirmation-dialog text for a consequence: its lines as one paragraph. */
export const consequenceMessage = (consequence: Consequence): string => consequence.lines.join(' ');

export default ConsequencePreview;
